import { Suspense, lazy, useEffect, useMemo } from "react";
import type { ComponentType } from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { MDXProvider } from "@mdx-js/react";
import { ArrowLeft, X } from "lucide-react";
import { useGuidesStore } from "@/stores/useGuidesStore";
import { getWalkthrough, resolveWalkthroughId } from "@/lib/guideRegistry";
import { TocProvider, useToc } from "@/components/guide/TocContext";
import { SoonStubOverlay } from "@/components/guide/SoonStubOverlay";
import { WalkthroughSkeleton } from "@/components/guide/WalkthroughSkeleton";
import { guideMdxComponents } from "@/components/guide/mdx-components";

export function WalkthroughOverlay() {
  const openId = useGuidesStore((s) => s.openWalkthroughId);
  const closeWalkthrough = useGuidesStore((s) => s.closeWalkthrough);

  if (!openId) return null;

  return (
    <DialogPrimitive.Root open={!!openId} onOpenChange={(o) => !o && closeWalkthrough()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-gray-900/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[92vw] max-w-[860px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl bg-guide-surface text-guide-text shadow-2xl focus:outline-none">
          <DialogPrimitive.Title className="sr-only">Walkthrough</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            PaperChase walkthrough content
          </DialogPrimitive.Description>
          <TocProvider>
            <WalkthroughBody openId={openId} />
          </TocProvider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function WalkthroughBody({ openId }: { openId: string }) {
  // openId may be a walkthrough id OR a feature/core-step id (e.g. from an
  // advisor deep-link); resolve it to a renderable walkthrough first.
  const resolvedId = resolveWalkthroughId(openId);
  const walkthrough = getWalkthrough(resolvedId);

  if (!walkthrough) return null;

  if (walkthrough.content.status === "soon") {
    return <SoonStubOverlay />;
  }

  if (!walkthrough.content.mdxModule) return null;

  return <LiveChapter openId={resolvedId} mdxModule={walkthrough.content.mdxModule} />;
}

// Extracted so the useMemo hook runs unconditionally (CLAUDE.md Rule #7).
function LiveChapter({
  openId,
  mdxModule,
}: {
  openId: string;
  mdxModule: () => Promise<{ default: ComponentType }>;
}) {
  const LazyChapter = useMemo(
    () => lazy(() => mdxModule().then((m) => ({ default: m.default }))),
    [mdxModule],
  );

  return (
    <div className="flex max-h-[85vh] min-h-[480px]">
      <TocSidebar walkthroughId={openId} />
      <MainPane>
        <Suspense fallback={<WalkthroughSkeleton />}>
          <MDXProvider components={guideMdxComponents}>
            <LazyChapter />
          </MDXProvider>
        </Suspense>
      </MainPane>
    </div>
  );
}

function TocSidebar({ walkthroughId }: { walkthroughId: string }) {
  const { entries, activeId, setActive, scrollRef } = useToc();
  const walkthrough = getWalkthrough(walkthroughId);
  const openWalkthrough = useGuidesStore((s) => s.openWalkthrough);
  const closeWalkthrough = useGuidesStore((s) => s.closeWalkthrough);
  const setCatalogOpen = useGuidesStore((s) => s.setCatalogOpen);

  if (!walkthrough) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setActive(id);
    const target = scrollRef.current?.querySelector(`#${id}`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleBrowseAll = () => {
    closeWalkthrough();
    setCatalogOpen(true);
  };

  return (
    <nav
      aria-label="In this guide"
      className="w-[230px] shrink-0 overflow-y-auto border-r border-guide-border bg-guide-panel p-5"
    >
      <button
        type="button"
        onClick={handleBrowseAll}
        className="mb-4 flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
      >
        <ArrowLeft className="size-3" />
        All guides
      </button>
      <div className="mb-3 text-[10px] font-bold uppercase tracking-wider text-guide-text-faint">
        In this guide
      </div>
      <ol className="space-y-1">
        {entries.map((e, i) => {
          const active = activeId === e.id;
          return (
            <li key={e.id}>
              <a
                href={`#${e.id}`}
                onClick={(ev) => handleClick(ev, e.id)}
                className={`block rounded px-2 py-1 text-xs ${
                  active
                    ? "bg-purple-100 font-semibold text-purple-900"
                    : "text-guide-text-muted hover:text-guide-text"
                }`}
              >
                {i + 1}. {e.title}
              </a>
            </li>
          );
        })}
      </ol>

      {(walkthrough.relatedWalkthroughs?.length ?? 0) > 0 && (
        <div className="mt-5 border-t border-guide-border-strong pt-4">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-guide-text-faint">
            Related
          </div>
          <div>
            {walkthrough.relatedWalkthroughs!.map((id) => {
              const w = getWalkthrough(id);
              if (!w) return null;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => openWalkthrough(id)}
                  className="block w-full text-left text-xs text-purple-600 hover:text-purple-800"
                >
                  → {w.content.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}

function MainPane({ children }: { children: React.ReactNode }) {
  const closeWalkthrough = useGuidesStore((s) => s.closeWalkthrough);
  const pendingSectionId = useGuidesStore((s) => s.pendingSectionId);
  const clearPendingSection = useGuidesStore((s) => s.clearPendingSection);
  const { scrollRef, entries, setActive } = useToc();

  // Deep-link: when openWalkthrough was called with a section id, scroll to it
  // once the section has mounted (its TOC entry registers on mount, so the
  // #id element is in the DOM by then). Then clear so it fires only once.
  useEffect(() => {
    if (!pendingSectionId) return;
    if (!entries.some((e) => e.id === pendingSectionId)) return;
    const target = scrollRef.current?.querySelector(`#${pendingSectionId}`);
    if (!target) return;
    setActive(pendingSectionId);
    requestAnimationFrame(() =>
      target.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
    clearPendingSection();
  }, [pendingSectionId, entries, scrollRef, setActive, clearPendingSection]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div className="sticky top-0 z-10 flex items-center justify-end bg-guide-surface px-5 pt-4">
        <button
          type="button"
          aria-label="Close walkthrough"
          onClick={closeWalkthrough}
          className="text-guide-text-faint hover:text-guide-text-soft"
        >
          <X className="size-5" />
        </button>
      </div>
      <div className="px-8 pb-6">{children}</div>
    </div>
  );
}
